import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Warning, MapPin } from '@phosphor-icons/react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const STYLE_URLS = {
  dark: 'mapbox://styles/mapbox/dark-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
};

// Fallback component when Mapbox token is invalid
const FallbackMap = ({ features }) => {
  const featureList = features?.features || [];
  
  // Group features by suite
  const grouped = featureList.reduce((acc, feature) => {
    const suite = feature.properties?.suite || 'Other';
    if (!acc[suite]) acc[suite] = [];
    acc[suite].push(feature);
    return acc;
  }, {});

  const suiteColors = {
    TerraVeritas: '#22C55E',
    UrbIntellect: '#3B82F6',
    AgriGrow: '#F5A623',
    GeoCore: '#FFFFFF',
    Other: '#10B981'
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#050505] relative overflow-hidden">
      {/* World grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#10B981" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Warning */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 glass-panel rounded-sm p-4 max-w-2xl z-10" data-testid="mapbox-warning">
        <div className="flex items-start gap-3">
          <Warning size={24} weight="duotone" className="text-amber-500 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-1">Mapbox Token Required</h4>
            <p className="text-xs text-gray-400">
              Set REACT_APP_MAPBOX_TOKEN in frontend/.env to enable interactive maps. 
              Get a free token at <span className="text-emerald-500">mapbox.com/account</span>. 
              Showing data overview below.
            </p>
          </div>
        </div>
      </div>

      {/* Feature visualization */}
      <div className="absolute inset-0 flex items-center justify-center p-8 pt-32">
        <div className="w-full max-w-6xl">
          <h2 className="text-2xl font-black text-white mb-6 text-center">Active Geospatial Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(grouped).map(([suite, items]) => (
              <div 
                key={suite} 
                className="glass-panel rounded-sm p-4 border-l-4"
                style={{ borderLeftColor: suiteColors[suite] }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: suiteColors[suite] }}
                  ></div>
                  <h3 className="font-bold text-white text-sm">{suite}</h3>
                </div>
                <p className="text-3xl font-black text-white mb-2">{items.length}</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Active Sites</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-white/5 rounded-sm">
                      <MapPin size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{item.properties?.name}</p>
                        <p className="text-[10px] text-gray-500">{item.properties?.category} • {item.properties?.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MapboxMap = ({ onMapReady, baseStyle = 'dark', features = null }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || hasError) return;
    
    if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('example')) {
      setHasError(true);
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    try {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: STYLE_URLS[baseStyle],
        center: [-98, 39],
        zoom: 3.5,
        pitch: 0
      });

      map.on('load', () => {
        setMapLoaded(true);
        if (onMapReady) {
          onMapReady(map);
        }
      });

      map.on('error', (e) => {
        if (e.error?.status === 401) {
          console.error('Mapbox token invalid');
          setHasError(true);
        }
      });

      // Add navigation controls
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      mapRef.current = map;
    } catch (err) {
      console.error('Map initialization failed:', err);
      setHasError(true);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [baseStyle, onMapReady, hasError]);

  // Update style when baseStyle changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    const targetStyle = STYLE_URLS[baseStyle];
    try {
      mapRef.current.setStyle(targetStyle);
    } catch (err) {
      console.error('Failed to set map style:', err);
    }
  }, [baseStyle, mapLoaded]);

  if (hasError) {
    return <FallbackMap features={features} />;
  }

  return (
    <div 
      ref={mapContainerRef} 
      className="w-full h-full"
      data-testid="mapbox-container"
    />
  );
};
